import { nextTick } from 'vue';

import type { IAppConfigResponse } from '@modules/config/types';
import type { IContextMutationResult } from '@modules/core/services/query/types';
import type { ISessionResponse } from '@modules/tokens/types';
import { CONFIG_QUERY_KEY } from '@modules/config/constants';
import { useRestClient } from '@modules/core/services/api';
import { useContextMutation } from '@modules/core/services/query';
import { I18N_GET_TRANSLATIONS_QUERY_KEY } from '@modules/i18n/constants';
import { useI18nStore } from '@modules/i18n/store';
import { HEADER_NOTIFICATION_QUERY_KEY } from '@modules/notification/constants';
import { PERMISSIONS_QUERY_KEY } from '@modules/permissions/constants';
import { TOKENS_QUERY_KEY } from '@modules/tokens/constants';
import { nextAnimationFrame } from '@utils/browser';
import { isString } from '@utils/guards';

import type { ISignOutPayload, ISignOutResponse } from '../types';
import {
  USER_SIGN_OUT_MUTATION_KEY,
  USER_SIGN_OUT_URL,
} from '../constants';

export default function useUserSignOutMutation(): IContextMutationResult<ISignOutResponse, Error, ISignOutPayload> {
  const { fetchDelete } = useRestClient();
  const { setLocale } = useI18nStore();

  return useContextMutation<ISignOutResponse, Error, ISignOutPayload>({
    mutationKey: [USER_SIGN_OUT_MUTATION_KEY],
    mutationFn: (queryParams) => fetchDelete<ISignOutResponse>(USER_SIGN_OUT_URL, { queryParams }),
    async onSuccess({ data: response, queryClient }) {
      await queryClient.invalidateQueries({ queryKey: [PERMISSIONS_QUERY_KEY] });

      queryClient.setQueryData<IAppConfigResponse>([CONFIG_QUERY_KEY], (data) => {
        if (!data) {
          return data;
        }

        return {
          ...data,
          current_user: response.user ?? null,
        };
      });

      queryClient.setQueryData<ISessionResponse>([TOKENS_QUERY_KEY], (data) => {
        if (!data) {
          return data;
        }

        return {
          ...data,
          session_token: response.session_token || data.session_token,
          supervision: undefined,
        };
      });

      if (response?.user?.locale) {
        await setLocale(response.user.locale);
      }

      await nextTick();
      await nextAnimationFrame();

      queryClient.removeQueries({
        predicate: (query) => !isString(query.queryKey[0]) || ![
          CONFIG_QUERY_KEY,
          PERMISSIONS_QUERY_KEY,
          TOKENS_QUERY_KEY,
          I18N_GET_TRANSLATIONS_QUERY_KEY,
          HEADER_NOTIFICATION_QUERY_KEY,
        ].includes(query.queryKey[0]),
      });
    },
  });
}
